
import { format } from 'date-fns';
import { MapPin, Calendar, Download } from 'lucide-react';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import { DocumentSubmission } from '@/hooks/useDocumentSubmissions';

interface DocumentInfoProps {
  document: DocumentSubmission;
  onDownload: () => void;
}

export function DocumentInfo({ document, onDownload }: DocumentInfoProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Document Details</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <h4 className="text-sm font-medium text-muted-foreground mb-1">File Name</h4>
          <p className="break-all">{document.file_name}</p>
        </div>
        
        <div>
          <h4 className="text-sm font-medium text-muted-foreground mb-1">Document Type</h4>
          <p className="capitalize">{document.document_type}</p>
        </div>
        
        {document.state && (
          <div>
            <h4 className="text-sm font-medium text-muted-foreground mb-1">State</h4>
            <p className="flex items-center">
              <MapPin className="h-3.5 w-3.5 mr-1 text-muted-foreground" />
              {document.state}
            </p>
          </div>
        )}
        
        {document.regulatory_authority && (
          <div>
            <h4 className="text-sm font-medium text-muted-foreground mb-1">Regulatory Authority</h4>
            <p>{document.regulatory_authority}</p>
          </div>
        )}
        
        {document.submission_period && (
          <div>
            <h4 className="text-sm font-medium text-muted-foreground mb-1">Reporting Period</h4>
            <p>{`${document.submission_period} ${document.submission_year || ''}`}</p>
          </div>
        )}
        
        <div>
          <h4 className="text-sm font-medium text-muted-foreground mb-1">Submitted On</h4>
          <p className="flex items-center">
            <Calendar className="h-3.5 w-3.5 mr-1 text-muted-foreground" />
            {format(new Date(document.submitted_at), 'MMM d, yyyy')}
          </p>
        </div>
        
        {document.file_size && (
          <div>
            <h4 className="text-sm font-medium text-muted-foreground mb-1">File Size</h4>
            <p>{(document.file_size / 1024 / 1024).toFixed(2)} MB</p>
          </div>
        )}
        
        {document.rejection_reason && (
          <div>
            <h4 className="text-sm font-medium text-muted-foreground mb-1">Rejection Reason</h4>
            <p className="text-red-600">{document.rejection_reason}</p>
          </div>
        )}
      </CardContent>
      <Separator />
      <CardFooter className="pt-4">
        <Button variant="outline" className="w-full" onClick={onDownload}>
          <Download className="mr-2 h-4 w-4" />
          Download File
        </Button>
      </CardFooter>
    </Card>
  );
}
